import React, { useState } from "react";
import PropTypes from "prop-types";
import { DateTime } from "luxon";
import styled from "styled-components";
import { Field } from "react-final-form";
import Pill from "components/Pill";
import Icon from "components/icons";
import Modal from "components/Modal";
import BusinessDetailLinks from "components/BusinessDetailLinks";
import ServicesTable from "components/ServicesTable";
import CancelAppointment from "components/CancelAppointment";
import CancelledMessage from "components/CancelledMessage";
import { FullAddressDropdown } from "dynamic";
import TextArea from "fields/TextAreaInput";
import SubmitButton from "components/SubmitButton";
import { formatPillTitle, handleStatus } from "lib/status-pill-helpers";
import CustomerAppointmentCard from "components/CustomerAppointmentCard";
import { canCustomerEdit, canCustomerCancel } from "lib/utils";
import { NOTES_TEXTAREA_CHAR_LIMIT } from "lib/constants";
import CustomerPromoCode from "forms/CustomerPromoCode";
import Alert from "components/Alert";
import Button from "components/Button";
import Link from "next/link";
import { formatCalDate, formatTimeAmPmFromISO } from "lib/date-helpers";

const StyledLink = styled.a`
  padding: 2rem;
  text-decoration: none;
  color: ${({ theme }) => theme.themeColors.primary};
  &:hover {
    text-decoration: underline;
  }
`;

const DetailWrapper = styled.div`
  max-width: 54.1875rem;
`;

const DetailTitle = styled.h2`
  font-size: 1.5rem;

  ${({ theme }) => theme.mq.lg`
    font-size: 2rem;
  `}
`;

const StatusPill = styled.div`
  align-items: center;
  display: inline-flex;
  font-size: 0.75rem;
  margin: 0 0 1rem;
`;

const Requested = styled.div`
  color: ${({ theme }) => theme.themeColors.darkGreen3};
  margin: 0 0 0 0.75rem;
`;

const SectionTitle = styled.h4`
  margin: 3rem 0 0.8rem;
`;

const CancelRequest = styled.button`
  background: transparent;
  border: 0 none;
  color: ${({ theme }) => theme.themeColors.darkGreen4};
  display: flex;
  font-size: 1rem;
  font-weight: 600;
  justify-content: center;
  margin: 3rem 0 0;
  padding: 0;

  :focus {
    outline: 0;
  }

  :focus-visible {
    outline: 0.125rem solid #0070f3;
  }
`;

const CircleIcon = styled(Icon)`
  margin: 0 0.5rem 0 0;
`;

const CustomerInformationContainer = styled.div`
  display: grid;
  row-gap: 2.5rem;
`;

const SubmitAndCancelContainer = styled.div`
  display: block;
  margin-top: 0.5rem;

  ${({ theme }) => theme.mq.ltSm`
display:flex;
flex-direction:column;

  `}
`;

const UnavailableAlert = styled(Alert)`
  /* margin-bottom: 2.125rem; */
`;

const UnavailableAlertContainer = styled.div`
  align-items: center;
  display: grid;

  ${({ theme }) => theme.mq.md`
    grid-template-columns: 1fr 11.75rem;
  `}
`;

const UnavailableAlertText = styled.p`
  color: ${({ theme }) => theme.themeColors.darkGreen1};
  font-size: 0.875rem;
  line-height: 1.125rem;
  margin-bottom: 0;
`;

const RequestInformationContainer = styled.div`
  padding: 20px 0;
`;

const RequestInformationSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const RequestInformationItem = styled.div`
  flex: 0 0 100%;

  ${({ theme }) => theme.mq.md`
    flex: 0 1 auto;
  `}
`;

const MutedText = styled.p`
  color: ${({ theme }) => theme.themeColors.darkGreen4};
`;

function CustomerAppointmentModal({
  data,
  close,
  showModal,
  setShowModal,
  reload,
  handleSubmit,
  fieldIndex,
  form,
}) {
  const { attributes, include } = data;
  const [showCancelAppointment, setShowCancelAppointment] = useState(false);
  const [promoCode, setPromoCode] = useState(
    include.appointmentPromoCodes?.data?.[0]?.attributes?.promoCodeName || ""
  ); // eslint-disable-line
  const [promoDiscount, setPromoDiscount] = useState(attributes.promoDiscount); // eslint-disable-line
  const [promoCodeError, setPromoCodeError] = useState(false); // eslint-disable-line
  const [total, setTotal] = useState(attributes.stripeCharge);
  const {
    cancellationFee,
    cancelledBy,
    createdAt,
    paymentStatus,
    skipPayments,
    requestedBy,
    customerUserPaymentMethod,
    confirmedAt,
  } = attributes;
  const appointmentStatus = attributes.status;
  const { vendorBusiness, appointmentLineItems, customerUser } = include;
  const {
    limitedStatusUpdates,
    skipNotes,
    skipCustomerStatusUpdates,
    businessName,
  } = vendorBusiness.attributes;
  const { name: customerName } = customerUser.attributes;
  const { data: servicesData } = appointmentLineItems;
  const appointmentLocation = attributes?.location;
  const isAtCustomersHouse = appointmentLocation === "customer_home";
  const canEdit = canCustomerEdit(appointmentStatus, limitedStatusUpdates);

  const clearPromoCode = () => {
    setPromoCode("");
    setTotal(total + promoDiscount);
    setPromoDiscount(0);
    form.change(
      `appointments.data[${fieldIndex}].attributes.promoCodeIds`,
      null
    );
    handleSubmit();
  };

  const promoCallback = (response) => {
    if (response?.data?.id) {
      form.change(`appointments.data[${fieldIndex}].attributes.promoCodeIds`, [
        response.data.id,
      ]);
      setPromoCodeError(false);
      const discount = attributes.subtotal * response.data.percent_discount;
      setPromoDiscount(discount);
      setTotal(total - discount);
    } else {
      setPromoCodeError(true);
    }
  };

  const promoCodeOnChange = (e) => {
    setPromoCode(e.target.value);
  };

  const cancelButton = () => {
    if (skipCustomerStatusUpdates || limitedStatusUpdates) {
      return null;
    }
    if (
      canCustomerCancel({ appointmentStatus, requestedBy }) &&
      !showCancelAppointment
    ) {
      return (
        <CancelRequest
          onClick={() => setShowCancelAppointment(!showCancelAppointment)}
        >
          <CircleIcon name="circle-close-24" color="#8F978C" />
          Cancel appointment
        </CancelRequest>
      );
    }
    return null;
  };

  const saveButton = () => {
    if (canEdit) {
      return (
        <SubmitAndCancelContainer>
          <SubmitButton
            type="button"
            onClick={async () => {
              handleSubmit();
              await reload();
            }}
          >
            Save
          </SubmitButton>
          {cancelButton()}
        </SubmitAndCancelContainer>
      );
    }
    return null;
  };

  const callback = async () => {
    await reload();
    setShowModal(false);
  };

  const cancelAppointment = () => {
    if (showCancelAppointment) {
      return (
        <CancelAppointment
          data={data}
          callback={callback}
          onClose={() => setShowCancelAppointment(false)}
        />
      );
    }
    return null;
  };

  const cancelledMessage = () => {
    if (
      appointmentStatus === "cancelled" &&
      cancelledBy?.indexOf("with_fee") > 0
    ) {
      return <CancelledMessage cancellationFee={cancellationFee} />;
    }
    return null;
  };

  const computedStatus = handleStatus({
    paymentStatus,
    appointmentStatus,
    skipPayments,
    userType: "customer",
    skipCustomerStatusUpdates,
  });

  const pillTitle = formatPillTitle({
    status: computedStatus,
  });

  const services = () => {
    const serviceFee = attributes.customerFeeInt;
    return (
      <ServicesTable
        discount={attributes.discountInt}
        isCustomer
        customerUserPaymentMethod={customerUserPaymentMethod}
        promoDiscount={promoDiscount}
        serviceFee={serviceFee}
        services={servicesData}
        skipPayments
        subtotal={attributes.subtotal}
        tipInt={attributes.tipInt}
        total={total}
        totalWithTip={attributes.totalCustomerAmountInt}
        computedStatus={computedStatus}
      />
    );
  };

  const detailTitle = () => {
    if (computedStatus === "expired") {
      return "Your appointment request has expired";
    }
    if (computedStatus === "pending") {
      if (requestedBy === "provider_request")
        return "An appointment has been set for you";
      return "Your appointment has been requested";
    }
    return `Your appointment ${
      computedStatus === "unpaid" ? "is" : "has been"
    } ${computedStatus}`;
  };

  const isApptConfirmed = (status) => {
    return (
      status === "approved" || status === "completed" || status === "checked-in"
    );
  };

  return (
    <Modal show={showModal} close={close}>
      <DetailWrapper>
        <StatusPill>
          <Pill status={computedStatus} title={pillTitle} />
          {createdAt && (
            <Requested>
              DATE OF REQUEST:{" "}
              {DateTime.fromISO(createdAt).toFormat("MM/dd/yyyy")}
            </Requested>
          )}
        </StatusPill>
        {computedStatus === "unavailable" ? (
          <DetailTitle>Please select a different time</DetailTitle>
        ) : (
          <DetailTitle>{detailTitle()}</DetailTitle>
        )}
        {computedStatus === "unavailable" && (
          <UnavailableAlert themeColor="primary">
            <UnavailableAlertContainer>
              <UnavailableAlertText>
                We’re sorry to tell you that the requested date/time is
                unavailable. Click a button if you want to make an appointment
                with the new schedule.
              </UnavailableAlertText>
              <Link href={attributes?.rescheduleLink} passHref>
                <Button colorType="primary" type="button" role="button">
                  Select a new time
                </Button>
              </Link>
            </UnavailableAlertContainer>
          </UnavailableAlert>
        )}
        <CustomerAppointmentCard
          appointment={data}
          showModal={showModal}
          form={form}
          index={fieldIndex}
          reload={reload}
          handleSubmit={handleSubmit}
          setShowModal={setShowModal}
        />
        <SectionTitle>Provider contact information</SectionTitle>
        <BusinessDetailLinks data={vendorBusiness} />
        <StyledLink href={vendorBusiness.attributes.externalLink} hidden>
          Book again
        </StyledLink>
        {services()}
        {!skipPayments && (
          <SectionTitle>Have a coupon/promo code?</SectionTitle>
        )}
        <CustomerPromoCode
          value={promoCode}
          disabled={!canEdit}
          error={promoCodeError}
          remove={clearPromoCode}
          callback={promoCallback}
          skipPayments={skipPayments}
          success={promoDiscount > 0}
          onChangeCallback={promoCodeOnChange}
          vendorBusinessId={vendorBusiness.id}
          customerUserId={attributes.customerUserId}
        />
        {(isAtCustomersHouse || !skipNotes) && (
          <SectionTitle>Customer Information</SectionTitle>
        )}
        <CustomerInformationContainer>
          {isAtCustomersHouse && (
            <Field
              width="100%"
              label="Home Address"
              name={`appointments.data[${fieldIndex}].attributes.addressPayload`}
              component={FullAddressDropdown}
              disabled={!canEdit}
            />
          )}
          {!skipNotes && (
            <Field
              width="100%"
              label="Notes for Groomer "
              name={`appointments.data[${fieldIndex}].attributes.customerNotes`}
              component={TextArea}
              disabled={!canEdit}
              inputLimit={NOTES_TEXTAREA_CHAR_LIMIT}
            />
          )}
        </CustomerInformationContainer>
        <RequestInformationContainer>
          <RequestInformationSection>
            <RequestInformationItem>
              <p>Created by:</p>
            </RequestInformationItem>
            <RequestInformationItem>
              <MutedText>
                {requestedBy === "provider_request"
                  ? businessName
                  : customerName}{" "}
                on {formatCalDate(createdAt)} at{" "}
                {formatTimeAmPmFromISO(createdAt)}
              </MutedText>
            </RequestInformationItem>
          </RequestInformationSection>
          {isApptConfirmed(computedStatus) && confirmedAt && (
            <RequestInformationSection>
              <RequestInformationItem>
                <p>Confirmed by:</p>
              </RequestInformationItem>
              <RequestInformationItem>
                <MutedText>
                  {requestedBy === "provider_request"
                    ? customerName
                    : businessName}{" "}
                  on {formatCalDate(confirmedAt)} at{" "}
                  {formatTimeAmPmFromISO(confirmedAt)}
                </MutedText>
              </RequestInformationItem>
            </RequestInformationSection>
          )}
        </RequestInformationContainer>
        {saveButton()}
        {cancelAppointment()}
        {cancelledMessage()}
      </DetailWrapper>
    </Modal>
  );
}

CustomerAppointmentModal.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string,
    include: PropTypes.shape({
      vendorBusiness: PropTypes.shape({
        attributes: PropTypes.object,
        id: PropTypes.string,
      }),
      appointmentLineItems: PropTypes.shape({ data: PropTypes.array }),
      customerUser: PropTypes.shape({
        id: PropTypes.string,
        attributes: PropTypes.shape({
          name: PropTypes.string,
        }),
      }),
      appointmentPromoCodes: PropTypes.shape({ data: PropTypes.array }),
    }),
    attributes: PropTypes.shape({
      cancelledBy: PropTypes.string,
      cancellationFee: PropTypes.string,
      cancellationNotes: PropTypes.string,
      createdAt: PropTypes.string,
      confirmedAt: PropTypes.string,
      customerFeeInt: PropTypes.number,
      customerNotes: PropTypes.string,
      customerUserId: PropTypes.string,
      customerUserPaymentMethod: PropTypes.shape({
        brand: PropTypes.string,
        last_four: PropTypes.string,
      }),
      discountInt: PropTypes.number,
      limitedStatusUpdates: PropTypes.bool,
      location: PropTypes.any,
      promoDiscount: PropTypes.number,
      paymentStatus: PropTypes.string,
      requestedBy: PropTypes.string,
      rescheduleLink: PropTypes.string,
      paymentLink: PropTypes.string,
      skipCustomerStatusUpdates: PropTypes.bool,
      skipPayments: PropTypes.bool,
      servicesAmountInt: PropTypes.number,
      status: PropTypes.string,
      stripeCharge: PropTypes.number,
      subtotal: PropTypes.number,
      tipInt: PropTypes.number,
      totalChargeInt: PropTypes.number,
      totalCustomerAmountInt: PropTypes.number,
    }),
  }),
  fieldIndex: PropTypes.number,
  form: PropTypes.object.isRequired,
  reload: PropTypes.func,
  handleSubmit: PropTypes.func,
  close: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({})]),
  showModal: PropTypes.bool,
  setShowModal: PropTypes.func,
};

CustomerAppointmentModal.defaultProps = {
  data: {},
  fieldIndex: undefined,
  reload: undefined,
  handleSubmit: undefined,
  close: () => {},
  showModal: false,
  setShowModal: undefined,
};

export default CustomerAppointmentModal;
